import React, { useState, useEffect, useMemo } from 'react'
import { PieChart, Pie, Cell } from 'recharts'
import { useIsMobile } from '../../../hooks/use-mobile'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import {
  ChartContainer
} from '../../../components/ui/chart'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip'

// Add shimmer animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = shimmerStyles;
  if (!document.head.querySelector('style[data-shimmer]')) {
    styleElement.setAttribute('data-shimmer', 'true');
    document.head.appendChild(styleElement);
  }
}

const RiskScore = () => {
  const isMobile = useIsMobile();
  const [chartKey, setChartKey] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRiskScore, setTotalRiskScore] = useState(0);

  // Simulate loading with dummy data
  useEffect(() => {
    setIsLoading(true);
    
    // Simulate API delay
    const timer = setTimeout(() => {
      setTotalRiskScore(0.80);
      setIsLoading(false);
    }, 600);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setChartKey(prev => prev + 1);
    }
  }, [totalRiskScore, isLoading]);

  // Function to get color theme based on risk score
  const getColorTheme = (riskScore) => {
    // riskScore is already a percentage (0.5117 = 51.17%), so multiply by 100 for display
    const scorePercent = riskScore * 100;
    
    if (scorePercent >= 70) {
      // High risk - Red theme
      return {
        primary: '#EF4444',
        secondary: 'rgba(239, 68, 68, 0.2)',
        hover: '#F87171'
      };
    } else if (scorePercent >= 40) {
      // Medium risk - Orange theme
      return {
        primary: '#F97316',
        secondary: 'rgba(249, 115, 22, 0.2)',
        hover: '#FB923C'
      };
    } else {
      // Low risk - Blue theme
      return {
        primary: '#3B82F6',
        secondary: 'rgba(59, 130, 246, 0.2)',
        hover: '#60A5FA'
      };
    }
  };

  // Function to get risk level text and color
  const getRiskLevelInfo = (riskScore) => {
    const scorePercent = riskScore * 100;
    const colorTheme = getColorTheme(riskScore);
    
    if (scorePercent >= 70) {
      return {
        text: 'High Risk',
        color: colorTheme.primary
      };
    } else if (scorePercent >= 40) {
      return {
        text: 'Medium Risk', 
        color: colorTheme.primary
      };
    } else {
      return {
        text: 'Low Risk',
        color: colorTheme.primary
      };
    }
  };

  // Memoize chart data for Recharts
  const chartData = useMemo(() => {
    const riskValue = Math.round(totalRiskScore * 100);
    const remainingValue = 100 - riskValue;
    const colorTheme = getColorTheme(totalRiskScore);
    
    return [
      { 
        name: 'RISK', 
        value: riskValue, 
        fill: colorTheme.primary
      },
      { 
        name: 'REMAINING', 
        value: remainingValue, 
        fill: colorTheme.secondary
      }
    ];
  }, [totalRiskScore]);

  // Helper function to adjust color brightness for hover effect
  const adjustColorBrightness = (color, isHovered) => {
    if (!isHovered) return color;
    
    const colorTheme = getColorTheme(totalRiskScore);
    
    // For the semi-transparent color, make it more opaque on hover
    if (color.includes('rgba')) {
      return color.replace('0.2', '0.4');
    }
    
    // Return the appropriate hover color based on current theme
    if (color === colorTheme.primary) {
      return colorTheme.hover;
    }
    
    return color;
  };

  // Chart configuration for shadcn/ui tooltips
  const chartConfig = useMemo(() => {
    const colorTheme = getColorTheme(totalRiskScore);
    return {
      value: {
        label: "Score",
      },
      RISK: {
        label: "Risk Score",
        color: colorTheme.primary,
      },
      REMAINING: {
        label: "Remaining", 
        color: colorTheme.secondary,
      },
    };
  }, [totalRiskScore]);

  return (
    <TooltipProvider>
      <Card className={`h-full bg-white border-[#CBC8C89C] flex flex-col relative ${isMobile ? 'rounded-xl p-2' : 'rounded-2xl p-3'}`}>
        <CardHeader className="pb-0 p-0">
          <CardTitle className={`font-semibold text-black font-inter tracking-[-0.025em] ${isMobile ? 'text-sm' : 'text-lg'}`}>
            Risk Score
          </CardTitle>
        </CardHeader>
        <CardContent className={`flex-1 flex justify-center items-center overflow-x-hidden p-0 ${isMobile ? 'min-h-[80px]' : 'min-h-[125px]'}`}>
        {isLoading ? (
          <div className="relative flex items-center justify-center" style={{ width: isMobile ? 150 : 240, height: isMobile ? 150 : 240 }}>
            {/* Pie chart shimmer skeleton */}
            <div className="relative rounded-full overflow-hidden" style={{ width: isMobile ? 120 : 210, height: isMobile ? 120 : 210 }}>
              {/* Background circle */}
              <div className="absolute inset-0 rounded-full bg-gray-100"></div>
              
              {/* Shimmer overlay */}
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
              </div>
            </div>
            
            {/* Inner white circle for donut effect */}
            <div 
              className="absolute rounded-full bg-white z-10" 
              style={{ 
                width: isMobile ? 80 : 150, 
                height: isMobile ? 80 : 150,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            >
              {/* Center text skeleton with pulse animation */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className={`bg-gray-200 rounded animate-pulse ${isMobile ? 'h-4 w-10' : 'h-6 w-14'}`}></div>
                <div className={`bg-gray-200 rounded animate-pulse mt-2 ${isMobile ? 'h-2 w-12' : 'h-3 w-16'}`}></div>
              </div>
            </div>
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square"
            style={{ width: isMobile ? 150 : 240, height: isMobile ? 150 : 240 }}
          >
            <PieChart key={chartKey}>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                innerRadius={isMobile ? 40 : 75}
                outerRadius={isMobile ? 60 : 105}
                startAngle={90}
                endAngle={-270}
                strokeWidth={0}
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={adjustColorBrightness(entry.fill, hoveredIndex === index)} 
                  />
                ))}
              </Pie>
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: isMobile ? "16px" : "24px", fontWeight: "600", fill: "#000" }}
              >
                {Math.round(totalRiskScore * 100)}%
              </text>
              <text
                x="50%"
                y="62%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: isMobile ? "9px" : "13px", fill: getRiskLevelInfo(totalRiskScore).color }}
              >
                {getRiskLevelInfo(totalRiskScore).text}
              </text>
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className={`absolute bottom-3 right-3 w-5 h-5 rounded-full border border-black/35 bg-white flex items-center justify-center text-black/35 hover:bg-gray-50 transition-colors ${isMobile ? 'text-xs' : 'text-sm'}`}>
            <span className="font-semibold">i</span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Risk score information will be displayed here</p>
        </TooltipContent>
      </Tooltip>
    </Card>
    </TooltipProvider>
  );
}

export default RiskScore